import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Send,
  Loader2,
  MessageCircle,
  CalendarDays,
  CheckCircle,
  RotateCcw,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api"; // Use centralized API client

export default function VoiceAgentChat() {
  const brand = useMemo(
    () => ({
      teal: "#55c5d0",
      tealHover: "#48b7c3",
      slateBg: "rgb(248,250,252)",
      border: "rgb(229,231,235)",
    }),
    []
  );

  // two allowed timezones
  const TIMEZONE_OPTIONS = [
    { label: "Austria", value: "Europe/Vienna" },
    { label: "Melbourne", value: "Australia/Melbourne" },
  ];

  // browser tz detection
  const BROWSER_TZ =
    Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "";

  const location = useLocation();
  const navigate = useNavigate();
  const chatScrollRef = useRef(null);
  const textareaRef = useRef(null);

  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState([]);

  const [serverSessionId, setServerSessionId] = useState(() => {
    const s = location.state?.sessionId;
    return typeof s === "number" && Number.isFinite(s) ? s : null;
  });
  // Secret returned by the start endpoint; the backend requires it on every
  // later call for this (anonymous) chat session.
  const [sessionToken, setSessionToken] = useState(
    () => location.state?.sessionToken || null
  );

  const [sessionLoading, setSessionLoading] = useState(false);
  const [showLeadConfirm, setShowLeadConfirm] = useState(false);
  const [showConfirmButton, setShowConfirmButton] = useState(false);
  const [leadGenerated, setLeadGenerated] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // loaders for submit buttons
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);

  // lock chat after successful schedule
  const [chatLocked, setChatLocked] = useState(false);

  const [scheduleForm, setScheduleForm] = useState({
    event_type: "",
    start: "",
    duration: "",
    timezone: "", // set default via effect below
    summary: "",
    location: "",
    description: "",
  });

  // set default timezone based on browser (Vienna/Melbourne) else Melbourne
  useEffect(() => {
    setScheduleForm((prev) => {
      if (prev.timezone) return prev;
      const preferred =
        BROWSER_TZ === "Europe/Vienna"
          ? "Europe/Vienna"
          : BROWSER_TZ === "Australia/Melbourne"
            ? "Australia/Melbourne"
            : "Australia/Melbourne";
      return { ...prev, timezone: preferred };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [sessionId] = useState(() => {
    const id = location.state?.sessionId;
    return typeof id === "number" ? id : Math.floor(Date.now());
  });

  const resolvedFlow = useMemo(() => {
    if (location.state?.flow) return String(location.state.flow).toLowerCase();
    const params = new URLSearchParams(location.search);
    const q = params.get("flow");
    if (q && ["buyer", "owner", "general"].includes(q.toLowerCase())) {
      return q.toLowerCase();
    }
    const vm = location.state?.voiceMessage?.toLowerCase() || "";
    if (vm.includes("selling")) return "owner";
    if (vm.includes("help")) return "general";
    return "buyer";
  }, [location.search, location.state]);

  const inputPlaceholder = useMemo(() => {
    if (chatLocked) return "Chat is locked after scheduling.";
    if (resolvedFlow === "owner") return "Type your selling question…";
    if (resolvedFlow === "general") return "Tell us how we can help…";
    return "Type your property question…";
  }, [resolvedFlow, chatLocked]);

  useEffect(() => {
    if (location.state?.voiceMessage) {
      setMessages((prev) => {
        const exists = prev.some(
          (m) => m.text === location.state.voiceMessage && m.role === "bot"
        );
        if (exists) return prev;
        return [
          ...prev,
          { id: Date.now(), role: "bot", text: location.state.voiceMessage },
        ];
      });
    }
  }, [location.state]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = "0px";
      el.style.overflow = "hidden";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [input]);



  const handleRestartSession = async () => {
    try {
      setSessionLoading(true);

      // Clear + UNLOCK chat
      setMessages([]);
      setShowLeadConfirm(false);
      setShowConfirmButton(false);
      setLeadGenerated(false);
      setShowScheduleModal(false);
      setChatLocked(false);
      setScheduleForm({
        event_type: "",
        start: "",
        duration: "",
        timezone:
          BROWSER_TZ === "Europe/Vienna"
            ? "Europe/Vienna"
            : "Australia/Melbourne",
        summary: "",
        location: "",
        description: "",
      });

      const res = await api.post(
        "api/chatbot/chat/start/",
        { flow: resolvedFlow },
        { timeout: 30000 }
      );

      const raw = res?.data ?? {};
      const sid =
        raw.session_id ??
        raw.sessionId ??
        raw.id ??
        raw.session?.id ??
        raw.data?.session_id ??
        raw.data?.sessionId ??
        null;

      setServerSessionId(
        sid != null && Number.isFinite(Number(sid)) ? Number(sid) : null
      );
      setSessionToken(raw.session_token ?? null);

      const fallbackWelcome =
        resolvedFlow === "owner"
          ? "What's the property address you're considering selling?"
          : resolvedFlow === "general"
            ? "How can we help you today?"
            : "Hi! How can I help with your property search?";

      const welcome = raw.message || raw.detail || fallbackWelcome;
      setMessages([{ id: Date.now(), role: "bot", text: welcome }]);
    } catch {
      setMessages([
        {
          id: Date.now(),
          role: "bot",
          text: "⚠️ Failed to restart session. Please try again.",
        },
      ]);
    } finally {
      setSessionLoading(false);
    }
  };

  const handleSend = async () => {
    if (chatLocked) return;
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    const userMsg = { id: Date.now(), role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsSending(true);

    try {
      const activeSessionId =
        serverSessionId != null ? serverSessionId : sessionId;

      const response = await api.post(
        "api/chatbot/chat/message/",
        {
          session_id: activeSessionId,
          session_token: sessionToken,
          message: trimmed,
          stream: false  // Disable streaming for non-streaming axios request
        },
        { timeout: 30000 }
      );

      // Handle response - backend returns { messages: [...], session_status: '...', is_qualified: bool }
      let botReply = "🤖 Sorry, I didn't quite get that — please try again.";

      if (response?.data?.messages && Array.isArray(response.data.messages)) {
        // Find the bot message from the messages array
        const botMessages = response.data.messages.filter(msg => msg.role === 'bot');
        if (botMessages.length > 0) {
          botReply = botMessages[botMessages.length - 1].content;
        }
      } else {
        // Fallback to old format for compatibility
        botReply = response?.data?.message ||
          response?.data?.reply ||
          response?.data?.response ||
          botReply;
      }

      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: "bot", text: botReply },
      ]);

      // Check if qualified to show confirm button
      if (response?.data?.is_qualified === true || response?.data?.complete === true) {
        setShowConfirmButton(true);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "⚠️ Something went wrong. Try again later.";

      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 2, role: "bot", text: errorMessage },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  // Confirm details (lead) submit with loader
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const fullName = e.target.full_name.value.trim();
    const email = e.target.email.value.trim();
    const contact = e.target.contact.value.trim();
    if (!fullName || !email || !contact) {
      alert("Please fill all fields");
      return;
    }

    try {
      setLeadSubmitting(true);
      await api.post(
        "api/chatbot/complete/",
        {
          session_id: serverSessionId ?? sessionId,
          session_token: sessionToken,
          full_name: fullName,
          email,
          contact,
        },
        { timeout: 15000 }
      );

      setShowLeadConfirm(false);
      setShowConfirmButton(false);
      setLeadGenerated(true);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 3,
          role: "bot",
          text: "✅ Lead generated successfully!",
        },
      ]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to submit details. Please try again.";
      alert(detail);
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Schedule submit with loader
  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      setScheduleSubmitting(true);
      const payload = {
        session_id: serverSessionId ?? sessionId,
        session_token: sessionToken,
        ...scheduleForm,
      };
      await api.post("api/chatbot/schedule/", payload);

      setShowScheduleModal(false);
      setLeadGenerated(false);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 4,
          role: "bot",
          text: "📅 Schedule created successfully!",
        },
      ]);

      setChatLocked(true);
    } catch {
      alert("Failed to schedule event. Try again.");
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const Message = ({ message }) => {
    const isUser = message.role === "user";
    return (
      <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
        {!isUser && (
          <div className="mr-3 mt-0.5 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
            <MessageCircle className="w-4 h-4" style={{ color: brand.teal }} />
          </div>
        )}
        <div
          className={`max-w-[90%] sm:max-w-[70%] px-4 py-2.5 text-[0.95rem] leading-relaxed rounded-2xl shadow-sm ${isUser
            ? "text-white rounded-br-md"
            : "text-slate-800 bg-white rounded-tl-md border border-gray-100"
            }`}
          style={isUser ? { background: brand.teal } : {}}
        >
          {message.text}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen font-inter" style={{ background: brand.slateBg }}>
      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
        {/* Top bar with RESTART button */}
        <div className="flex items-center justify-start mb-6">
          <button
            onClick={handleRestartSession}
            disabled={sessionLoading}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium shadow-sm ${sessionLoading ? "bg-gray-400 cursor-not-allowed" : "bg-[#55c5d0] hover:bg-[#48b7c3]"
              }`}
            title="Restart (clears chat and starts a fresh session)"
          >
            {sessionLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Restarting…
              </>
            ) : (
              <>
                <RotateCcw className="w-4 h-4" />
                Restart Session
              </>
            )}
          </button>
        </div>

        {/* Chat card */}
        <div
          className="bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col"
          style={{ borderColor: brand.border, height: "72vh" }}
        >
          <div
            ref={chatScrollRef}
            className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5 bg-slate-50/70"
          >
            {messages.map((m) => (
              <Message key={m.id} message={m} />
            ))}

            {showConfirmButton && !leadGenerated && !chatLocked && (
              <div className="flex justify-center">
                <button
                  onClick={() => setShowLeadConfirm(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#55c5d0] hover:bg-[#48b7c3] text-white font-medium rounded-full shadow-sm transition"
                >
                  <CheckCircle className="w-5 h-5" />
                  Confirm Your Details
                </button>
              </div>
            )}

            {/* After lead is confirmed but before scheduling: show BOTH buttons in same row */}
            {leadGenerated && !chatLocked && (
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="inline-flex items-center gap-2 bg-[#55c5d0] hover:bg-[#48b7c3] text-white px-5 py-2.5 rounded-lg font-medium shadow-sm transition"
                >
                  <CalendarDays className="w-4 h-4" />
                  Schedule Call / Inspection
                </button>

                <button
                  onClick={() => navigate("/property-view-listing")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium shadow-sm border border-[#55c5d0] text-[#55c5d0] bg-white hover:bg-[#ecfeff] transition"
                >
                  Back to Properties
                </button>
              </div>
            )}

            {/* After schedule success → show Scheduled (disabled) + Back in SAME row */}
            {chatLocked && (
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium shadow-sm bg-emerald-500/90 text-white cursor-default"
                  title="Event scheduled"
                >
                  <CheckCircle className="w-4 h-4" />
                  Scheduled
                </button>

                <button
                  onClick={() => navigate("/property-view-listing")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium shadow-sm border border-[#55c5d0] text-[#55c5d0] bg-white hover:bg-[#ecfeff] transition"
                >
                  Back to Properties
                </button>
              </div>
            )}

            {isSending && !chatLocked && (
              <div className="flex justify-start">
                <div className="mr-3 mt-0.5 w-8 h-8 rounded-full bg-white flex items-center justify-center border border-gray-200 shadow-sm">
                  <MessageCircle className="w-4 h-4 animate-pulse" style={{ color: brand.teal }} />
                </div>
                <div className="max-w-[70%] bg-white rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm border border-gray-100">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400 inline-block mr-2" />
                  Agent is typing...
                </div>
              </div>
            )}
          </div>

          {/* Input bar (disabled when chatLocked) */}
          <div className="border-t bg-white p-4 sm:p-5" style={{ borderColor: brand.border }}>
            <div className="flex items-end gap-3">
              <div className="relative flex-1">
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={inputPlaceholder}
                  className={`resize-none overflow-hidden w-full min-h-[44px] pl-4 pr-14 py-3 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none ${chatLocked ? "opacity-60 cursor-not-allowed" : "focus:ring-2"
                    }`}
                  style={{
                    borderColor: brand.border,
                    "--tw-ring-color": brand.teal,
                  }}
                  disabled={isSending || chatLocked}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending || input.trim() === "" || chatLocked}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-lg text-white inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm hover:shadow"
                  style={{
                    backgroundColor:
                      isSending || input.trim() === "" || chatLocked ? "#72d2dc" : brand.teal,
                  }}
                  title={chatLocked ? "Chat is locked after scheduling" : "Send"}
                >
                  {isSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Lead Confirmation Modal */}
      {showLeadConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-2xl shadow-xl w/full max-w-md border border-gray-100">
            <h2 className="text-base font-semibold text-slate-900">Confirm Your Details</h2>
            <form onSubmit={handleLeadSubmit} className="space-y-3 mt-3">
              <input
                name="full_name"
                placeholder="Full Name"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
              />
              <input
                name="email"
                placeholder="Email"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
              />
              <input
                name="contact"
                placeholder="Phone Number"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowLeadConfirm(false)}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                  disabled={leadSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={leadSubmitting}
                  className="bg-[#55c5d0] text-white px-4 py-2 rounded-lg hover:bg-[#48b7c3] inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {leadSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Modal (with loading on submit) */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 sm:p-7 rounded-2xl shadow-xl w-full max-w-2xl border border-gray-100">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Schedule Call / Inspection
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Choose event details below, then click{" "}
                <span className="font-medium text-slate-700">Schedule</span>.
              </p>
            </div>

            <form onSubmit={handleScheduleSubmit} className="space-y-5">
              {/* Row 1: Event Type / Timezone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Event Type
                  </label>
                  <select
                    value={scheduleForm.event_type}
                    onChange={(e) =>
                      setScheduleForm({ ...scheduleForm, event_type: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
                  >
                    <option value="">Select type…</option>
                    <option value="call">Call</option>
                    <option value="inspection">Inspection</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Timezone
                  </label>
                  <select
                    required
                    value={scheduleForm.timezone}
                    onChange={(e) =>
                      setScheduleForm({ ...scheduleForm, timezone: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
                  >
                    <option value="" disabled>
                      Select timezone…
                    </option>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Start / Duration */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Start (Date & Time)
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduleForm.start}
                    onChange={(e) => {
                      setScheduleForm({ ...scheduleForm, start: e.target.value });
                      e.target.blur();
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    placeholder="Duration (minutes)"
                    value={scheduleForm.duration}
                    onChange={(e) =>
                      setScheduleForm({ ...scheduleForm, duration: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
                  />
                </div>
              </div>

              {/* Row 3: Summary / Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Summary
                  </label>
                  <input
                    placeholder="Collings Appointment"
                    value={scheduleForm.summary}
                    onChange={(e) =>
                      setScheduleForm({ ...scheduleForm, summary: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Location
                  </label>
                  <input
                    placeholder="Washington"
                    value={scheduleForm.location}
                    onChange={(e) =>
                      setScheduleForm({ ...scheduleForm, location: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
                  />
                </div>
              </div>

              {/* Row 4: Description (full width) */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Description"
                  value={scheduleForm.description}
                  onChange={(e) =>
                    setScheduleForm({ ...scheduleForm, description: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]"
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="px-4 py-2.5 rounded-lg border hover:bg-gray-50"
                  disabled={scheduleSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={scheduleSubmitting}
                  className="bg-[#55c5d0] text-white px-4 py-2.5 rounded-lg hover:bg-[#48b7c3] inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {scheduleSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Scheduling…
                    </>
                  ) : (
                    "Schedule"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
